import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    input,
    Output,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LayoutModule, SharedUiModule } from '@pf/shared-ui';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import {
    CpwBoxType,
    CpwPackingFormComponent,
    CpwPackingFormValue,
} from './cpw-packing-form.component';
import {
    CpwPackageType,
    CpwPackingCustomFields,
    CpwPackingItem,
    isHandlingLtl,
} from '@cpw/domain';
import {
    PackingListDto,
    PackingListFacade,
    PackLineDto,
    PackLineFacade,
} from '@pf/platform-wms/domain';
import { CategoryFacade } from '@pf/platform-item-catalog/domain';
import {
    CustomFieldDto,
    getCustomFieldValue,
    mergeCustomFields,
} from '@pf/shared/util-platform';
import { concat, concatMap, forkJoin, of, tap, toArray } from 'rxjs';
import {
    ItemDimensionUnitOfMeasure,
    PackingStatus,
    WeightUnitOfMeasure,
} from '@control-tower/platform-wms';
import { asArraySafe } from '@pf/shared-utility';

@Component({
    selector: 'cpw-packing-workflow',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        SharedUiModule,
        LayoutModule,
        NzRadioModule,
        CpwPackingFormComponent,
    ],
    templateUrl: './cpw-packing-workflow.component.html',
    styleUrl: './cpw-packing-workflow.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwPackingWorkflowComponent {
    private readonly categoryFacade = inject(CategoryFacade);
    private readonly packingListFacade = inject(PackingListFacade);
    private readonly packLineFacade = inject(PackLineFacade);

    style = input<'mobile' | 'desktop'>('mobile');
    itemsToPack = input<CpwPackingItem[]>([]);
    disabled = input<boolean>(false);
    packages = input<PackingListDto[] | null>(null);
    projectId = input<string | null>(null);
    warehouseId = input<string | null>(null);

    boxTypes = signal<CpwBoxType[]>([]);
    saving = signal(false);

    @Output() packagesChange = new EventEmitter<PackingListDto[]>();
    packagesConfirmed = output<PackingListDto[]>();

    readonly packagesInternal = signal<PackingListDto[]>([]);
    readonly packageValidity = signal<Record<string, boolean>>({});
    private readonly packageTypeMap = signal<Record<string, CpwPackageType>>(
        {}
    );

    readonly totalPackages = computed(() => this.packagesInternal().length);

    readonly remainingItems = computed(() => {
        const allItems = this.itemsToPack();
        if (allItems.length === 0) {
            return [];
        }

        const packages = this.packagesInternal();

        return allItems
            .map(item => {
                const used = packages.reduce((sum, pkg) => {
                    const matchingLines =
                        pkg.packLines?.filter(
                            packLine =>
                                packLine.bundle?.item?.id === item.itemId
                        ) ?? [];
                    return (
                        sum +
                        matchingLines.reduce(
                            (lineSum, pl) => lineSum + (pl.packedQuantity ?? 0),
                            0
                        )
                    );
                }, 0);
                return {
                    ...item,
                    quantityToPack: Math.max(item.quantityToPack - used, 0),
                };
            })
            .filter(item => item.quantityToPack > 0);
    });

    readonly allItemsPacked = computed(() => {
        const items = this.itemsToPack();
        if (items.length === 0) {
            return false;
        }

        const packages = this.packagesInternal();
        if (packages.length === 0) {
            return false;
        }

        if (this.remainingItems().length > 0) {
            return false;
        }

        const validity = this.packageValidity();
        return packages.every(pkg => validity[pkg.packingListNumber] === true);
    });

    readonly canAddPackage = computed(() => {
        if (this.disabled()) {
            return false;
        }

        const items = this.itemsToPack();
        if (items.length === 0) {
            return true;
        }

        const hasEmptyPackage = this.packagesInternal().some(
            pkg => !pkg.packLines?.length
        );

        return this.remainingItems().length > 0 && !hasEmptyPackage;
    });

    readonly inputStyle = computed(() => {
        return this.style() === 'mobile' ? 'compact' : 'block';
    });

    readonly packageViewModels = computed(() => {
        const packages = this.packagesInternal();
        const typeMap = this.packageTypeMap();
        const allItems = this.itemsToPack();
        const remaining = this.remainingItems();

        return packages.map(pkg => {
            const packageType: CpwPackageType =
                typeMap[pkg.packingListNumber] ?? 'box';

            const availableItems =
                allItems.length === 0
                    ? []
                    : allItems
                          .map(item => {
                              const usedByThis = (
                                  pkg.packLines?.filter(
                                      pl => pl.bundle?.item?.id === item.itemId
                                  ) ?? []
                              ).reduce(
                                  (sum, pl) => sum + (pl.packedQuantity ?? 0),
                                  0
                              );
                              const remainingItem = remaining.find(
                                  ri => ri.itemId === item.itemId
                              );
                              const available =
                                  usedByThis +
                                  (remainingItem?.quantityToPack ?? 0);
                              return { ...item, quantityToPack: available };
                          })
                          .filter(item => item.quantityToPack > 0);

            return { pkg, packageType, availableItems };
        });
    });

    constructor() {
        effect(() => {
            const packages = this.packages();
            if (packages) {
                this.packagesInternal.set(packages);

                this.setInitialPackageTypeMap(packages);
            }
        });
        this.getBoxTypes();
    }

    addPackage() {
        if (!this.canAddPackage()) {
            return;
        }

        const used = this.packagesInternal()
            .map(p => parseInt(p.packingListNumber, 10))
            .filter(n => !isNaN(n));
        const packingListNumber = used.length === 0 ? 1 : Math.max(...used) + 1;

        const pkg = new PackingListDto({
            projectId: this.projectId(),
            warehouse: { id: this.warehouseId() as string },
            packLines: [],
            packingListNumber: packingListNumber.toString(),
        });

        this.packagesInternal.update(list => [...list, pkg]);
        this.packageTypeMap.update(map => ({
            ...map,
            [packingListNumber.toString()]: 'box',
        }));
    }

    setPackageType(packingListNumber: string, type: CpwPackageType) {
        this.packageTypeMap.update(map => ({
            ...map,
            [packingListNumber]: type,
        }));
        this.packagesInternal.update(list =>
            list.map(p => {
                if (p.packingListNumber !== packingListNumber) return p;
                return new PackingListDto({
                    ...p,
                    dimensions: type === 'pallet' ? undefined : p.dimensions,
                    item: type === 'box' ? p.item : undefined,
                });
            })
        );
    }

    removePackage(packingListNumber: string) {
        this.packagesInternal.update(list => {
            const next = list.filter(
                p => p.packingListNumber !== packingListNumber
            );
            this.packagesChange.emit(next);
            return next;
        });
        this.packageValidity.update(v => {
            const next = { ...v };
            delete next[packingListNumber];
            return next;
        });
        this.packageTypeMap.update(map => {
            const next = { ...map };
            delete next[packingListNumber];
            return next;
        });
    }

    onPackageValidChange(id: string, valid: boolean) {
        this.packageValidity.update(v => ({ ...v, [id]: valid }));
    }

    onPackageValueChange(id: string, value: CpwPackingFormValue) {
        this.packagesInternal.update(list => {
            const next = list.map(p => {
                if (p.packingListNumber === id) {
                    return new PackingListDto({
                        ...p,
                        item: value.selectedBox
                            ? {
                                  id:
                                      this.boxTypes().find(
                                          b => b.code === value.selectedBox
                                      )?.id ?? value.selectedBox,
                              }
                            : undefined,
                        packLines: value.packLines.map(packLine => {
                            const projectLineId = this.itemsToPack().find(
                                item =>
                                    item.itemId === packLine.bundle?.item?.id
                            )?.projectLineItemId;
                            if (projectLineId) {
                                packLine.projectLineItem = {
                                    id: projectLineId,
                                };
                            }
                            return packLine;
                        }),
                        weight: value.weightLbs
                            ? {
                                  unitOfMeasure: WeightUnitOfMeasure.Pounds,
                                  pounds: value.weightLbs,
                              }
                            : undefined,
                        dimensions:
                            value.lengthIn && value.widthIn && value.heightIn
                                ? {
                                      unitOfMeasure:
                                          ItemDimensionUnitOfMeasure.Inches,
                                      length: value.lengthIn
                                          ? { inches: value.lengthIn }
                                          : undefined,
                                      width: value.widthIn
                                          ? { inches: value.widthIn }
                                          : undefined,
                                      height: value.heightIn
                                          ? { inches: value.heightIn }
                                          : undefined,
                                  }
                                : undefined,
                    });
                }
                return p;
            });

            this.packagesChange.emit(next);
            return next;
        });
    }

    confirmPackages() {
        const packages = this.packagesInternal();

        if (packages.length === 0) {
            return;
        }

        this.saving.set(true);

        const typeMap = this.packageTypeMap();
        const packagesWithCustomFields = packages.map(pkg => {
            const packageType =
                typeMap[pkg.packingListNumber] ??
                (pkg.item?.id ? 'box' : 'pallet');
            return new PackingListDto({
                ...pkg,
                customFields: mergeCustomFields(asArraySafe(pkg.customFields), [
                    CpwPackingWorkflowComponent.buildPackageTypeCustomField(
                        packageType
                    ),
                ]),
            });
        });

        const saves$ = packagesWithCustomFields.map(pkg => {
            pkg.status = PackingStatus.Packed;
            return this.packingListFacade.save$(pkg).pipe(
                concatMap(savedList => {
                    const packLineSaves$ = pkg.packLines.map(packLine => {
                        const updatedPackLine = new PackLineDto({
                            ...packLine,
                            status: PackingStatus.Packed,
                            packingListId: savedList.id,
                            packedQuantity: packLine.packedQuantity,
                            packingListDate: new Date().toISOString(),
                            bundle: packLine.bundle,
                        });
                        return this.packLineFacade.save$(updatedPackLine);
                    });

                    if (packLineSaves$.length === 0) {
                        return of(savedList);
                    }

                    // Save pack lines sequentially one at a time
                    return concat(...packLineSaves$).pipe(
                        toArray(),
                        tap(savedLines => {
                            savedList.packLines = savedLines;
                        }),
                        concatMap(() => of(savedList))
                    );
                })
            );
        });

        forkJoin(saves$).subscribe({
            next: savedPackingLists => {
                this.saving.set(false);
                this.packagesInternal.set(savedPackingLists);
                this.packagesConfirmed.emit(savedPackingLists);
            },
            error: () => {
                this.saving.set(false);
            },
        });
    }

    private getBoxTypes() {
        this.categoryFacade // TODO: refactor this once we have a better way to get box types
            .getRelatedItems$('5F80C4B2-7FD6-4982-2A0D-08DE6E3CF709')
            .pipe(
                tap(itemResult => {
                    if (itemResult.data) {
                        this.boxTypes.set(
                            itemResult.data.map(item => ({
                                id: item.id,
                                type: item.name,
                                code: item.code || item.sku || item.name,
                                isCustomBox: item.code === 'wm-box-custom',
                                lengthIn: item.dimensions?.length?.inches,
                                widthIn: item.dimensions?.width?.inches,
                                heightIn: item.dimensions?.height?.inches,
                                weightLbs: item.weight?.pounds,
                            }))
                        );
                    }
                })
            )
            .subscribe();
    }

    private setInitialPackageTypeMap(packages: PackingListDto[]) {
        const hasItemWithLtl = this.itemsToPack().some(item =>
            item.bundles.some(
                bundle =>
                    (bundle.container &&
                        isHandlingLtl(
                            bundle.container?.equipment?.equipmentType
                        )) ||
                    isHandlingLtl(bundle.container?.slot?.equipmentType)
            )
        );
        const defaultPackingType = hasItemWithLtl ? 'pallet' : 'box';
        const typeMap: Record<string, CpwPackageType> = {};
        for (const pkg of packages) {
            const persistedType =
                CpwPackingWorkflowComponent.getPackageTypeFromCustomFields(
                    pkg.customFields
                );
            typeMap[pkg.packingListNumber] =
                persistedType ??
                (pkg.packLines?.length
                    ? pkg.item?.id
                        ? 'box'
                        : 'pallet'
                    : defaultPackingType);
        }
        this.packageTypeMap.set(typeMap);
    }

    private static getPackageTypeFromCustomFields(
        customFields: Array<CustomFieldDto> | null | undefined
    ): CpwPackageType | undefined {
        const value = getCustomFieldValue<string>(
            customFields,
            CpwPackingCustomFields.PackageType
        );
        return value === 'box' || value === 'pallet' ? value : undefined;
    }

    private static buildPackageTypeCustomField(
        packageType: CpwPackageType
    ): CustomFieldDto {
        return new CustomFieldDto({
            key: CpwPackingCustomFields.PackageType,
            value: packageType,
        });
    }
}
